import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchFeedAround,
  fetchFeedPage,
  fetchMessageById,
} from "@/api/feed";
import {
  isFeedApiUnavailable,
  loadStaticFeedArchive,
  sliceFeedAround,
  sliceFeedPage,
  sliceFeedPageAfter,
} from "@/api/feedStaticFallback";
import type { FeedMessage } from "@/types";
import { messageIdKey } from "@/utils/messageId";
import { sortFeedMessages } from "@/utils/feedSort";

const PAGE_SIZE = 100;
const ANCHOR_PAGE_SIZE = 30;
/** Сколько старше показать сразу; новее — только по прокрутке вниз. */
const JUMP_BEFORE = 10;
const JUMP_AFTER = 0;
/** Стабильные индексы Virtuoso при подгрузке вверх. */
export const VIRTUOSO_START_INDEX = 100_000;

function mergeById(
  map: Map<string, FeedMessage>,
  list: FeedMessage[]
): Map<string, FeedMessage> {
  const next = new Map(map);
  for (const m of list) next.set(messageIdKey(m.id), m);
  return next;
}

function mergeMessages(
  prev: FeedMessage[],
  incoming: FeedMessage[],
  prepend: boolean
): FeedMessage[] {
  const combined = prepend ? [...incoming, ...prev] : [...prev, ...incoming];
  const seen = new Set<string>();
  return combined.filter((m) => {
    const k = messageIdKey(m.id);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export function useFeed() {
  const [messages, setMessages] = useState<FeedMessage[]>([]);
  const [byId, setById] = useState<Map<string, FeedMessage>>(new Map());
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [hasMoreNewer, setHasMoreNewer] = useState(false);
  const [atLatest, setAtLatest] = useState(true);
  const [firstItemIndex, setFirstItemIndex] = useState(VIRTUOSO_START_INDEX);
  const messagesRef = useRef<FeedMessage[]>([]);
  messagesRef.current = messages;
  const hasMoreRef = useRef(false);
  hasMoreRef.current = hasMore;
  const hasMoreNewerRef = useRef(false);
  hasMoreNewerRef.current = hasMoreNewer;
  const staticArchiveRef = useRef<FeedMessage[] | null>(null);

  const applyPage = useCallback(
    (
      page: {
        messages: FeedMessage[];
        has_more: boolean;
        has_more_newer?: boolean;
      },
      opts?: { atLatest?: boolean }
    ) => {
      const merged = sortFeedMessages(mergeMessages([], page.messages, false));
      setMessages(merged);
      setById(mergeById(new Map(), page.messages));
      setHasMore(page.has_more);
      setHasMoreNewer(page.has_more_newer ?? false);
      const latest =
        opts?.atLatest ?? !(page.has_more_newer ?? false);
      setAtLatest(latest);
      messagesRef.current = merged;
      hasMoreRef.current = page.has_more;
      hasMoreNewerRef.current = page.has_more_newer ?? false;
      setFirstItemIndex(VIRTUOSO_START_INDEX);
    },
    []
  );

  /** Заменить ленту окном вокруг якоря (без слияния с хвостом «последних N»). */
  const applyAnchoredWindow = useCallback(
    (page: {
      messages: FeedMessage[];
      has_more: boolean;
      has_more_newer?: boolean;
    }) => {
      const merged = sortFeedMessages(page.messages);
      setMessages(merged);
      setById(mergeById(new Map(), page.messages));
      setHasMore(page.has_more);
      setHasMoreNewer(page.has_more_newer ?? false);
      setAtLatest(false);
      messagesRef.current = merged;
      hasMoreRef.current = page.has_more;
      hasMoreNewerRef.current = page.has_more_newer ?? false;
      setFirstItemIndex(VIRTUOSO_START_INDEX);
    },
    []
  );

  const loadInitial = useCallback(async () => {
    try {
      const page = await fetchFeedPage(PAGE_SIZE);
      staticArchiveRef.current = null;
      applyPage(page, { atLatest: true });
    } catch (e) {
      if (!isFeedApiUnavailable(e)) throw e;
      const archive = await loadStaticFeedArchive();
      staticArchiveRef.current = archive;
      applyPage(sliceFeedPage(archive, PAGE_SIZE), { atLatest: true });
    }
  }, [applyPage]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        await loadInitial();
        if (!cancelled) setError(null);
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Ошибка загрузки ленты"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [loadInitial]);

  const loadOlder = useCallback(async () => {
    if (loadingMore || !hasMore || messagesRef.current.length === 0) return;

    const oldest = messagesRef.current[0];
    if (oldest == null) return;

    setLoadingMore(true);
    try {
      const limit = atLatest ? PAGE_SIZE : ANCHOR_PAGE_SIZE;
      let page;
      if (staticArchiveRef.current) {
        page = sliceFeedPage(staticArchiveRef.current, limit, {
          beforeId: oldest.id,
          beforeDate: oldest.date,
        });
      } else {
        page = await fetchFeedPage(limit, {
          beforeId: oldest.id,
          beforeDate: oldest.date,
        });
      }

      if (page.messages.length === 0) {
        setHasMore(false);
        hasMoreRef.current = false;
        return;
      }

      const prevLen = messagesRef.current.length;
      const merged = sortFeedMessages(
        mergeMessages(messagesRef.current, page.messages, true)
      );
      const added = merged.length - prevLen;

      setMessages(merged);
      setById((m) => mergeById(m, page.messages));
      setHasMore(page.has_more);
      messagesRef.current = merged;
      hasMoreRef.current = page.has_more;
      if (added > 0) {
        setFirstItemIndex((i) => i - added);
      }
      setError(null);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Не удалось подгрузить старые сообщения"
      );
    } finally {
      setLoadingMore(false);
    }
  }, [atLatest, hasMore, loadingMore]);

  const loadNewer = useCallback(async () => {
    if (loadingMore || !hasMoreNewer || messagesRef.current.length === 0) {
      return;
    }

    const newest = messagesRef.current[messagesRef.current.length - 1];
    if (newest == null) return;

    setLoadingMore(true);
    try {
      const limit = ANCHOR_PAGE_SIZE;
      let page;
      if (staticArchiveRef.current) {
        page = sliceFeedPageAfter(staticArchiveRef.current, limit, {
          afterId: newest.id,
          afterDate: newest.date,
        });
      } else {
        page = await fetchFeedPage(limit, {
          afterId: newest.id,
          afterDate: newest.date,
        });
      }

      if (page.messages.length === 0) {
        setHasMoreNewer(false);
        hasMoreNewerRef.current = false;
        setAtLatest(true);
        return;
      }

      const merged = sortFeedMessages(
        mergeMessages(messagesRef.current, page.messages, false)
      );
      const moreNewer = page.has_more_newer ?? false;

      setMessages(merged);
      setById((m) => mergeById(m, page.messages));
      setHasMoreNewer(moreNewer);
      if (!moreNewer) setAtLatest(true);
      messagesRef.current = merged;
      hasMoreNewerRef.current = moreNewer;
      setError(null);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Не удалось подгрузить новые сообщения"
      );
    } finally {
      setLoadingMore(false);
    }
  }, [hasMoreNewer, loadingMore]);

  const jumpToLatest = useCallback(async () => {
    setLoadingMore(false);
    setFirstItemIndex(VIRTUOSO_START_INDEX);
    await loadInitial();
  }, [loadInitial]);

  const ensureMessage = useCallback(async (id: string, fallback?: FeedMessage) => {
    const key = messageIdKey(id);
    if (messagesRef.current.some((m) => messageIdKey(m.id) === key)) {
      return;
    }
    const msg = (await fetchMessageById(key)) ?? fallback;
    if (!msg) return;
    const merged = sortFeedMessages(
      mergeMessages(messagesRef.current, [msg], false)
    );
    setMessages(merged);
    setById((m) => mergeById(m, [msg]));
    messagesRef.current = merged;
  }, []);

  /** Окно ±JUMP_CONTEXT вокруг сообщения; дальше — только по прокрутке. */
  const jumpToMessage = useCallback(
    async (id: string, fallback?: FeedMessage): Promise<boolean> => {
      const key = messageIdKey(id);

      if (messagesRef.current.some((m) => messageIdKey(m.id) === key)) {
        return true;
      }

      try {
        const page =
          staticArchiveRef.current
            ? sliceFeedAround(
                staticArchiveRef.current,
                key,
                JUMP_BEFORE,
                JUMP_AFTER
              )
            : await fetchFeedAround(key, JUMP_BEFORE, JUMP_AFTER);
        if (page.messages.length === 0) {
          const msg = (await fetchMessageById(key)) ?? fallback;
          if (!msg) return false;
          applyAnchoredWindow({
            messages: [msg],
            has_more: true,
            has_more_newer: true,
          });
          return true;
        }
        applyAnchoredWindow(page);
        return page.messages.some((m) => messageIdKey(m.id) === key);
      } catch {
        const msg = (await fetchMessageById(key)) ?? fallback;
        if (!msg) return false;
        applyAnchoredWindow({
          messages: [msg],
          has_more: true,
          has_more_newer: true,
        });
        return true;
      }
    },
    [applyAnchoredWindow, atLatest]
  );

  const removeMessage = useCallback((id: string) => {
    const key = messageIdKey(id);
    setMessages((prev) => prev.filter((m) => messageIdKey(m.id) !== key));
    setById((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
    messagesRef.current = messagesRef.current.filter(
      (m) => messageIdKey(m.id) !== key
    );
  }, []);

  return {
    messages,
    byId,
    loading,
    loadingMore,
    error,
    hasMore,
    hasMoreNewer,
    atLatest,
    loadOlder,
    loadNewer,
    jumpToLatest,
    firstItemIndex,
    ensureMessage,
    jumpToMessage,
    removeMessage,
  };
}
